import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'

import Box from '@components/kit/box'
import Card from '@components/kit/card'
import { Column, Row } from '@components/kit/grid'
import Loading from '@components/kit/loading'
import Text from '@components/kit/text'

import { ChatContext } from '../chat.web'
import { useHealthTrackingStore } from '@store/HealthTracking'
import { IHealthTrackingBloodSugar } from '@store/HealthTracking/types'
import Pressable from '@components/kit/pressable'
import { ChevronDownIcon } from '@components/kit/icons'

const PAGE_SIZE = 30
const RETRACTED_PAGE_SIZE = 10

function BloodSuggarLog() {
  const { userId } = useContext(ChatContext)
  const { getBloodSugarLog } = useHealthTrackingStore.getState()
  const [bloodSugarLog, setBloodSugarLog] =
    useState<IHealthTrackingBloodSugar | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [currentPageSize, setCurrentPageSize] =
    useState<number>(RETRACTED_PAGE_SIZE)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const isRetractable = bloodSugarLog?.results.length > RETRACTED_PAGE_SIZE

  const toggleExpanded = () => {
    if (isExpanded) {
      setCurrentPageSize(RETRACTED_PAGE_SIZE)
    } else {
      setCurrentPageSize(PAGE_SIZE)
    }
    setIsExpanded(!isExpanded)
  }

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      const data = await getBloodSugarLog(userId, currentPage, PAGE_SIZE)
      setBloodSugarLog(data)
      setIsLoading(false)
    }
    fetchData()
  }, [userId, currentPage])

  return (
    <React.Fragment>
      <Row
        mt='24'
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <Text ml='12' mb='08' font='display.16.bold'>
          Blood Sugar Log
        </Text>

        {isRetractable && (
          <Pressable
            mr='12'
            onPress={toggleExpanded}
            width='20'
            height='20'
            justifyContent='center'
            alignItems='center'
            style={{ transform: [{ rotate: !isExpanded ? '0deg' : '180deg' }] }}
          >
            <ChevronDownIcon fill='shade07' width={20} height={20} />
          </Pressable>
        )}
      </Row>
      <Card mt='08' px='00' py='00'>
        {isLoading ? (
          <Box pt='24'>
            <Loading mt='20' py='20' />
          </Box>
        ) : (
          <React.Fragment>
            <Row m='16'>
              <Column width='33%'>
                <Text font='text.12.bold'>Date</Text>
              </Column>
              <Column width='33%'>
                <Text font='text.12.bold'>Time</Text>
              </Column>
              <Column width='33%'>
                <Text font='text.12.bold'>Blood Sugar (mg/dl)</Text>
              </Column>
            </Row>

            {bloodSugarLog?.results.length === 0 && (
              <Row mx='16' my='12'>
                <Text font='text.14.medium'>No data available yet.</Text>
              </Row>
            )}

            {bloodSugarLog?.results.map((bloodSugar, index) => {
              if (index >= currentPageSize) {
                return null
              }
              const isRecent =
                moment().diff(moment(bloodSugar.timestamp), 'days') <= 7
              return (
                <Row key={index} mx='16' my='12'>
                  <Column width='33%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {moment(
                        bloodSugar.timestamp,
                        'YYYY-MM-DDTHH:mm:ssZ'
                      ).format('MM/DD/YYYY')}
                    </Text>
                  </Column>
                  <Column width='33%' flexDirection='row' alignItems='center'>
                    <Text font='text.14.medium'>
                      {moment(
                        bloodSugar.timestamp,
                        'YYYY-MM-DDTHH:mm:ssZ'
                      ).format('hh:mm A')}
                    </Text>
                  </Column>
                  <Column width='33%' flexDirection='row' alignItems='center'>
                    {isRecent && (
                      <Box
                        backgroundColor='walrusBlue500'
                        width='12px'
                        height='12px'
                        mr='08'
                        borderRadius='xlarge'
                      />
                    )}
                    <Text font='text.14.medium'>{bloodSugar.value}</Text>
                  </Column>
                </Row>
              )
            })}
          </React.Fragment>
        )}
      </Card>
    </React.Fragment>
  )
}

export default BloodSuggarLog
